// Inlined wire types for the widget. Mirrors @support-agent/shared/schemas/chat
// without depending on the workspace package — the widget bundle has to ship
// to a Shopify storefront and pulling in zod just for type validation isn't
// worth the bundle weight. The server validates with zod; the widget trusts.

export const CHAT_PROTOCOL_VERSION = 1;

export interface SupportAgentCtx {
  shop: string;
  customerId: string | number | null;
  customerEmail: string | null;
  customerName?: string | null;
  /** Override the API origin. Defaults to the script tag's origin. */
  apiOrigin?: string;
  // ─── Merchant-configurable appearance (set by the Shopify app embed) ───────
  /** Accent color for the bubble/header/outbound bubbles. Default #111. */
  accentColor?: string | null;
  /** Which corner the bubble sits in. Default 'right'. */
  position?: 'left' | 'right' | null;
  /** Header title text. Default 'Chat with us'. */
  title?: string | null;
  /** Intro line shown in an empty conversation, before the first message. */
  greeting?: string | null;
  /**
   * When set, the `/widget/start` handshake is POSTed same-origin to
   * `${location.origin}${proxyPath}/widget/start` (Shopify App Proxy) instead
   * of directly to the API — so Shopify signs a verified logged-in customer id.
   * The live WebSocket still connects to `apiOrigin`. Default: unset (direct).
   */
  proxyPath?: string | null;
  /** The shop's privacy policy. Default: the store's own /policies/privacy-policy (every Shopify store has one). */
  privacyUrl?: string | null;
}

export interface WidgetStartResponse {
  token: string;
  threadId: string;
  expiresAt: string;
}

/** Who wrote a reply: the AI assistant or an employee (first name). */
export interface MessageAuthor {
  kind: 'ai' | 'agent';
  name?: string;
}

/** A file an employee sent in the chat. `url` is relative to the API origin. */
export interface ChatAttachment {
  url: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
}

/** ai = the assistant answers; waiting = handed to a person; human = a person answers. */
export type ChatMode = 'ai' | 'waiting' | 'human';

export type ServerFrame =
  | {
      v: number;
      type: 'welcome';
      threadId: string;
      customerId: string | null;
      /** The customer proved their email (code, or Shopify login via the App Proxy). */
      identityVerified?: boolean;
      verifiedEmail?: string | null;
      aiAssistant?: boolean;
      chatMode?: ChatMode;
      /** The session was renewed on connect: it now lasts until then. */
      sessionExpiresAt?: string;
      history: Array<{
        id: string;
        direction: 'INBOUND' | 'OUTBOUND';
        body: string;
        receivedAt: string;
        /** Our own id for a customer message — matches an unsent copy. */
        externalMessageId?: string;
        author?: MessageAuthor;
        attachments?: ChatAttachment[];
      }>;
    }
  | {
      v: number;
      type: 'message';
      threadId: string;
      messageId: string;
      direction: 'INBOUND' | 'OUTBOUND';
      body: string;
      receivedAt: string;
      /** Set on the echo of the customer's own message (its client id). */
      externalMessageId?: string;
      author?: MessageAuthor;
      attachments?: ChatAttachment[];
    }
  | {
      v: number;
      type: 'mode';
      threadId: string;
      mode: ChatMode;
    }
  | {
      v: number;
      type: 'typing';
      threadId: string;
      who: 'customer' | 'agent';
      isTyping: boolean;
    }
  | {
      v: number;
      type: 'error';
      code: string;
      message: string;
    };

export interface UiMessage {
  id: string;
  direction: 'INBOUND' | 'OUTBOUND';
  body: string;
  receivedAt: string;
  /** True for messages typed locally and not yet acked by the server. */
  pending?: boolean;
  author?: MessageAuthor;
  /** Files sent with the message, with absolute URLs. */
  attachments?: ChatAttachment[];
}
